import { EASE } from "@nnf3d/lib";
import { Link, useSearch } from "@tanstack/react-router";
import { useStore } from "@tanstack/react-store";
import { AnimatePresence, m } from "motion/react";
import { useCallback } from "react";

import { useGlobal } from "../lib/global-context";
import { loadStore, routeStore, tooltipStore } from "../lib/store";
import { hideTooltip, showTooltip } from "../lib/tooltip";
import type { VoidComponent } from "../lib/types";
import { booleanAttribute } from "../lib/utils";
import { Button } from "./button";
import { SoundToggleButton } from "./sound-toggle-button";

const aboutSearch = { about: 1 } as const;

const DesktopNavigation: VoidComponent = () => {
	const { aboutLinkText, foundationLink, foundationTitle } = useGlobal();
	const search = useSearch({ from: "__root__" });

	const isStarted = useStore(loadStore, (state) => state.isStarted);
	const level = useStore(routeStore, (state) => state.level);

	const handleCopyLink = useCallback(() => {
		navigator.clipboard
			.writeText(globalThis.location.href)
			.then(() => {
				tooltipStore.setState("Copied link");

				return null;
			})
			.catch(console.error);
	}, []);

	if (!isStarted) {
		return null;
	}

	const headerVariants = {
		initial: { opacity: 0, y: "1rem" },
		animate: { opacity: 1, y: "0", transition: { duration: 2, ease: EASE.OUT_EXPO, delay: 0.6 } },
	} as const satisfies Parameters<typeof m.header>[0];

	const copyLinkAnimation = {
		initial: { opacity: 0, y: "-0.25rem" },
		animate: { opacity: 1, y: "0" },
		exit: { opacity: 0, y: "-0.25rem" },
		transition: { type: "spring", visualDuration: 0.8, bounce: 0, restDelta: 0.001 },
	} as const satisfies Parameters<typeof m.div>[0];

	return (
		<m.header {...headerVariants} className="absolute top-8 right-8 z-130 hidden desktop:block">
			<nav className="flex h-12 items-center gap-0.5">
				<AnimatePresence>
					{level !== "index" && (
						<m.div
							{...copyLinkAnimation}
							className="data-about:*:bg-white"
							data-about={booleanAttribute(search?.about === 1)}
						>
							<Button
								icon="link"
								aria-label="Copy link"
								onClick={handleCopyLink}
								onMouseEnter={showTooltip("Copy link")}
								onMouseLeave={hideTooltip()}
							/>
						</m.div>
					)}
				</AnimatePresence>

				<SoundToggleButton />

				<Link to="." search={search?.about === 1 ? undefined : aboutSearch}>
					<Button data-active={booleanAttribute(search?.about === 1)} tabIndex={-1}>
						{aboutLinkText}
					</Button>
				</Link>

				<a
					href={foundationLink}
					target="_blank"
					rel="noopener noreferrer"
					className="data-about:*:bg-white"
					data-about={booleanAttribute(search?.about === 1)}
				>
					<Button icon="arrow-up-right" tabIndex={-1}>
						{foundationTitle}
					</Button>
				</a>
			</nav>
		</m.header>
	);
};

export { DesktopNavigation };
